import { type Course, type CourseSection, UserAction } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import clsx from "clsx";
import { setCookie } from "cookies-next";
import { memo } from "react";
import type { KeyedMutator } from "swr";

import CourseSectionBody from "./CourseSectionBody";
import CourseSectionHeader from "./CourseSectionHeader";
import type { FocusSection } from "./CourseSections";
import type { useUserAction } from "~/hooks";
import { useAccess } from "~/hooks";
import useFreeCourse from "~/hooks/useFreeCourse";
import useTrackCTA from "~/hooks/useTrackCTA";
import fetcher from "~/utils/fetcher.client";

export interface CourseSectionProps {
	course: Course;
	section: CourseSection;
	hasCourseAccess: boolean;
	className?: string;
	nextSection?: CourseSection;
	handleExpandSection: (section: CourseSection) => void;
	handleCollapseSection: (section: CourseSection) => void;
	isFreeSection?: boolean;
	isFirstSection?: boolean;
	mutateVisitedSections: KeyedMutator<string[]>;

	isExpanded: boolean;
	isVisited: boolean;
	isCompleted: boolean;

	visitedSectionKeys: string[] | undefined;
	expandedSectionKeys: string[];
	completedSectionKeys: string[] | undefined;
	focusSections: FocusSection[];

	isMember: boolean;
	userAction: ReturnType<typeof useUserAction>["userAction"];
	setUserAction: ReturnType<typeof useUserAction>["setUserAction"];
}

const CourseSection = (props: CourseSectionProps) => {
	const {
		course,
		section,

		className,

		handleCollapseSection,
		handleExpandSection,
		mutateVisitedSections,

		hasCourseAccess,
		isFreeSection,
		isExpanded,

		setUserAction,

		isMember,

		visitedSectionKeys,
		expandedSectionKeys,
		completedSectionKeys,
		focusSections,
	} = props;

	const { isSignedIn, userId, actor } = useAuth();
	const { isLoadingAccess } = useAccess();

	const trackCTA = useTrackCTA();
	const { openClaimIfOffered } = useFreeCourse(course._id);

	const toggleIsExpanded = (section: CourseSection) => {
		if (isFreeSection || hasCourseAccess) {
			if (isExpanded && userId && !actor) {
				mutateVisitedSections(
					fetcher(`/courses/${course.slug}/visited/sections`, {
						method: "POST",
						data: {
							sectionKey: section._key,
						},
					}),
				);
			}

			if (isExpanded) {
				handleCollapseSection(section);
			} else {
				handleExpandSection(section);
			}
		} else if (!isSignedIn) {
			trackCTA(UserAction.SIGN_UP, "Course Section");

			setCookie("audited-course-id", course._id);
			setUserAction(UserAction.SIGN_UP);
		} else if (isLoadingAccess) {
			return;
		} else if (!isMember) {
			if (openClaimIfOffered()) return;

			trackCTA(UserAction.BECOME_A_MEMBER, "Course Section");

			setUserAction(UserAction.BECOME_A_MEMBER);
		}
	};

	return (
		<>
			<div
				id={section._key}
				key={`${section._key}-component`}
				className={clsx(
					"relative flex w-full scroll-m-[7.5rem] flex-col",
					{
						"course-section": !isFreeSection,
					},
					className,
				)}
			>
				<CourseSectionHeader {...props} toggleIsExpanded={toggleIsExpanded} />

				{isExpanded && !!section.body?.length && (
					<CourseSectionBody {...props} toggleIsExpanded={toggleIsExpanded} />
				)}
			</div>

			{isExpanded && !!section.subSections?.length && (
				<div className="flex w-full flex-col gap-4 phablet:gap-6 phablet:pl-3.5 pl-2">
					{section.subSections.map((subSection, i) => (
						<CourseSection
							{...props}
							section={{ ...subSection, parent: section }}
							key={subSection._key}
							isVisited={!!visitedSectionKeys?.includes(subSection._key)}
							isExpanded={expandedSectionKeys.includes(subSection._key)}
							isCompleted={!!completedSectionKeys?.includes(subSection._key)}
							focusSections={focusSections}
							nextSection={section.subSections?.[i + 1]}
						/>
					))}
				</div>
			)}
		</>
	);
};

export default memo(CourseSection);
